"use client";

import { Core } from "@faire/design-tokens";
import { Typography } from "@faire/web--source/slate/Typography";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, {
  css,
  DefaultTheme as StyledComponentsTheme,
} from "styled-components";

import { ColorOrKey } from "../Color";
import { themeOrDefault } from "../Theme";
import {
  HIGH_VIS_FILTER,
  highVisSlateComponent,
} from "../utils/highVisSlateComponent";

const RADIO_DIAMETER = 16;

/**
 * @deprecated use `Radio` from `@faire/slate/components/radio` - since 2025-03-28
 *
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/radio)
 */
export const Radio: React.FC<Radio.IProps> = (props) => {
  const {
    active = false,
    align,
    children,
    className,
    color,
    disabled = false,
    disabledColor,
    error = false,
    label,
    name,
    onClick,
    secondaryLabel,
  } = props;

  const labelId = useUniqueId("radio-button-label-");

  return (
    <RadioWrapper $align={align} className={className}>
      <RadioInnerWrapper>
        <RadioInput
          $active={active}
          $align={align}
          aria-label={props["aria-label"]}
          aria-labelledby={props["aria-labelledby"]}
          checked={active}
          color={color}
          data-test-id={props["data-test-id"]}
          disabled={disabled}
          $disabledColor={disabledColor}
          $error={error}
          id={labelId}
          name={name}
          onChange={onClick}
          type="radio"
          readOnly={onClick === undefined}
        />
      </RadioInnerWrapper>
      {/* It's important to wrap the children in a label so that the label connects to the input via id & makes it clickable*/}
      {children || label ? (
        <Label disabled={disabled} htmlFor={labelId}>
          {children || (
            <LabelText
              align="left"
              color={color}
              $disabled={disabled}
              $disabledColor={disabledColor}
              $error={error}
              id={labelId}
            >
              {label}
              <SecondaryLabel>{secondaryLabel}</SecondaryLabel>
            </LabelText>
          )}
        </Label>
      ) : null}
    </RadioWrapper>
  );
};

export const SecondaryLabel = styled(Typography).attrs({
  color: Core.text.subdued,
  forwardedAs: "span",
  variant: "paragraphSansRegular",
})`
  display: block;
`;

const RadioWrapper = styled.div.attrs({ role: "presentation" })<{
  $align?: "flex-start" | "center";
}>`
  align-items: ${(props) => props.$align ?? "center"};
  display: flex;
  ${highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT)};
`;

const RadioInnerWrapper = styled.div`
  align-items: center;
  display: flex;
  height: ${({ theme }) => theme?.fonts?.sansSerif?.p?.lineHeight || "12px"};
`;

const RadioInput = styled.input<{
  $active: boolean;
  $align?: "flex-start" | "center";
  $disabledColor?: string;
  $error?: boolean;
}>`
  appearance: none;
  border: 1px solid
    ${({
      color,
      disabled: disabledProp,
      $disabledColor: disabledColor,
      $error: errorProp,
      theme,
    }) => {
      const {
        radios: { enabled },
      } = themeOrDefault(theme);
      if (disabledProp) {
        return disabledColor || Core.border.muted;
      } else if (errorProp) {
        return Core.border.critical;
      } else {
        return color ?? enabled;
      }
    }};
  block-size: ${RADIO_DIAMETER}px;
  border-radius: 50%;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  inline-size: ${RADIO_DIAMETER}px;
  margin: 0;
  min-block-size: ${RADIO_DIAMETER}px;
  min-inline-size: ${RADIO_DIAMETER}px;
  position: relative;
  background-color: ${({
    disabled: disabledProp,
    $active: active,
    $disabledColor: disabledColor,
  }) => {
    if (disabledProp && !active) {
      return disabledColor || Core.surface.subdued;
    } else {
      return;
    }
  }};
  &:focus-visible {
    outline: revert;
  }

  // Fill in the radio input circle when it's selected
  ${({
    $active: active,
    color,
    disabled,
    $disabledColor: disabledColor,
    $error: error,
    theme,
  }) => {
    const { radios } = themeOrDefault(theme);

    let activeBackgroundColor;
    if (disabled) {
      activeBackgroundColor = disabledColor ?? radios.disabled;
    } else if (error) {
      activeBackgroundColor = Core.text.critical;
    } else {
      activeBackgroundColor = color ?? radios.enabled;
    }

    return active
      ? css`
          &:before {
            background-color: ${activeBackgroundColor};
            block-size: ${RADIO_DIAMETER / 2}px;
            border-radius: 50%;
            content: " ";
            inline-size: ${RADIO_DIAMETER / 2}px;
            inset-block-start: 3px;
            inset-inline-start: 3px;
            position: absolute;
          }
        `
      : "";
  }};
`;

export const Label = styled.label<{
  disabled?: boolean;
}>`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  padding-inline-start: 8px;
`;

export const LabelText = styled(Typography)<{
  $disabled?: boolean;
  $disabledColor?: any;
  $error?: boolean;
}>`
  &&& {
    color: ${({ $disabled, $error, theme, color, $disabledColor }) => {
      const { radios } = themeOrDefault(theme);
      let labelTextColor;

      if ($disabled) {
        labelTextColor = $disabledColor || radios.disabled;
      } else if ($error) {
        labelTextColor = Core.text.critical;
      } else {
        labelTextColor = color || radios.enabled;
      }
      return labelTextColor;
    }};
  }
`;

export default Radio;

export namespace Radio {
  export interface IProps {
    active?: boolean;
    align?: "flex-start" | "center";
    "aria-label"?: string;
    "aria-labelledby"?: string;
    children?: React.ReactNode;
    className?: string;
    color?: ColorOrKey;
    "data-test-id"?: string;
    disabled?: boolean;
    disabledColor?: string;
    error?: boolean;
    label?: string | React.ReactNode;
    name?: string; // Todo: `name` needs to be required, FD-93610
    onClick?: () => void;
    secondaryLabel?: string;
    theme?: StyledComponentsTheme;
  }
}
